import { useEffect, useState, useRef, useMemo } from 'react';

/**
 * Custom hook to get the size of a DOM element or the window size.
 *
 * @returns {Object} An object containing the width and height of the element and window.
 */
export function useScreenSize() {
    const ref = useRef<HTMLDivElement | null>(null);
    const [elementSize, setElementSize] = useState({ width: 0, height: 0 });
    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

    const breakpoint = useMemo(() => {
        if (ref.current) {
            return getBreakpoint(elementSize.width);
        }
        return getBreakpoint(windowSize.width);
    }, [elementSize.width, windowSize.width]);

    useEffect(() => {
        const updateSizes = () => {
            if (ref.current) {
                const { offsetWidth, offsetHeight } = ref.current;
                setElementSize({ width: offsetWidth, height: offsetHeight });
            }
            setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        };

        // Get initial sizes on mount
        updateSizes();

        const handleResize = () => updateSizes();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return { ref, elementSize, windowSize, isSmallScreen: breakpoint === "xs" || breakpoint === "sm" };
}

function getBreakpoint(width: number) {
    if (width < 600) return 'xs';
    if (width < 960) return 'sm';
    if (width < 1280) return 'md';
    if (width < 1920) return 'lg';
    return 'xl';
}

export default useScreenSize;
